/**
 * 商务风尚模版 - 首页
 *
 * 设计理念：深墨绿 Hero 头条 + 商务统计 + 左右分栏
 * 与 sunset（浅色搜索框 + 分类网格 + 瀑布流）完全不同的排版骨架：
 * - 顶部：深墨绿渐变 Hero 头条区（左文案+搜索，右统计卡）
 * - 统计：金色细线 + 数据统计条
 * - 分类：商务白卡网格（方角 + 墨绿图标底）
 * - 最新：左右分栏（左 2/3 卡片列表，右 1/3 置顶侧栏）
 * - 底部：金色分隔 + 工具入口
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/templates/executive/components/Navbar";
import ExecutiveFooter from "@/templates/executive/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function ExecutiveHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 12)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 16 })
      .then((data) => {
        setListings(data.list || []);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const pinnedListings = listings.filter(l => l.is_pinned);
  const normalListings = listings.filter(l => !l.is_pinned);

  return (
    <div className="min-h-screen bg-[#F5F6F4]">
      <Navbar />

      {/* ======== 第一部分：深墨绿 Hero 头条区 ======== */}
      <section className="relative bg-gradient-to-br from-[#1F4E3D] via-[#1A4334] to-[#12382A]">
        {/* 金色装饰线 */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C2A04B] to-transparent" />
        {/* 背景纹理 */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 14px)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            {/* 左：文案 + 搜索 */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#C2A04B]/15 border border-[#C2A04B]/40 mb-6">
                <i className="ri-medal-line text-[#DDC583] text-sm" />
                <span className="text-xs font-semibold text-[#DDC583] tracking-wide uppercase">专业分类信息服务</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
                高效发布，
                <span className="text-[#DDC583]">精准触达</span>
                <br />
                让信息创造价值
              </h1>
              <p className="text-white/70 text-sm sm:text-base mb-8 max-w-xl leading-relaxed">
                覆盖多行业分类信息，商家与用户的专业对接平台。严谨审核、规范运营，保障每一份信息真实可靠。
              </p>

              <form onSubmit={handleSearch} className="max-w-xl">
                <div className="flex items-center bg-white rounded-md shadow-2xl shadow-black/20 overflow-hidden focus-within:ring-2 focus-within:ring-[#C2A04B]">
                  <div className="flex-1 flex items-center pl-5">
                    <i className="ri-search-line text-[#C2A04B] text-xl mr-3" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="搜索您需要的信息..."
                      className="flex-1 py-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
                    />
                  </div>
                  <button
                    type="submit"
                    className="shrink-0 px-8 py-4 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-bold hover:from-[#D8B86A] hover:to-[#B8912F] transition-all text-sm"
                  >
                    <i className="ri-search-line mr-1.5" />搜索
                  </button>
                </div>
                {showSearchTip && (
                  <div className="mt-3 px-4 py-2.5 bg-[#C2A04B] text-[#1F4E3D] text-sm rounded-sm shadow-lg inline-block">
                    <i className="ri-error-warning-line mr-1" />{searchConfig.disabledMessage || "搜索功能已关闭"}
                  </div>
                )}
              </form>
            </div>

            {/* 右：服务承诺卡 */}
            <div className="lg:col-span-2">
              <div className="bg-white/95 rounded-md border border-[#C2A04B]/30 p-6 shadow-2xl shadow-black/20">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#E8E3D5]">
                  <span className="w-1 h-5 bg-[#C2A04B] rounded-full" />
                  <span className="text-sm font-bold text-[#1F4E3D] tracking-wide">服务承诺</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-sm bg-[#1F4E3D] flex items-center justify-center text-[#DDC583]">
                      <i className="ri-shield-check-line text-base" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1F4E3D]">严格审核</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">专业团队人工审核，保障信息真实可信</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-sm bg-[#1F4E3D] flex items-center justify-center text-[#DDC583]">
                      <i className="ri-file-lock-line text-base" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1F4E3D]">隐私保护</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">敏感信息加密存储，安心发布放心浏览</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-sm bg-[#1F4E3D] flex items-center justify-center text-[#DDC583]">
                      <i className="ri-team-line text-base" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1F4E3D]">专业运营</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">深耕分类信息领域，规范化运营管理</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#A8863A] flex items-center justify-center text-[#1F4E3D]">
                      <i className="ri-customer-service-2-line text-base" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1F4E3D]">专属服务</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">一对一客服响应，全程协助无忧发布</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======== 第二部分：分类 商务白卡网格 ======== */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="w-1 h-6 bg-[#C2A04B] rounded-full" />
              <h2 className="text-xl font-black text-[#1F4E3D]">服务分类</h2>
            </div>
            <Link to="/category" className="text-sm text-[#8C6E2E] hover:text-[#C2A04B] font-medium flex items-center gap-1">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingCategories ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse aspect-square bg-gray-200/70 rounded-md" />
              ))}
            </div>
          ) : (
            <div className={`grid gap-3 ${
              categories.length <= 3 ? 'grid-cols-3' :
              categories.length <= 4 ? 'grid-cols-4' :
              categories.length <= 6 ? 'grid-cols-3 sm:grid-cols-6' :
              'grid-cols-4 sm:grid-cols-6 lg:grid-cols-8'
            }`}>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center justify-center gap-2 p-3 sm:p-4 bg-white rounded-md border border-[#E2E8E0] hover:border-[#C2A04B] hover:shadow-md hover:-translate-y-1 transition-all duration-300 aspect-square group"
                >
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-sm bg-[#1F4E3D] flex items-center justify-center text-[#DDC583] group-hover:bg-[#C2A04B] group-hover:text-[#1F4E3D] transition-colors">
                    <i className={`${cat.icon || 'ri-folder-line'} text-lg sm:text-xl`} />
                  </div>
                  <span className="text-[11px] sm:text-xs text-gray-600 font-medium text-center line-clamp-1 group-hover:text-[#1F4E3D]">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ======== 第三部分：最新信息 左右分栏 ======== */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="w-1 h-6 bg-[#C2A04B] rounded-full" />
              <h2 className="text-xl font-black text-[#1F4E3D]">最新发布</h2>
            </div>
            <Link to="/category" className="text-sm text-[#8C6E2E] hover:text-[#C2A04B] font-medium flex items-center gap-1">
              浏览全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 左 2/3：信息卡片列表 */}
            <div className="lg:col-span-2">
              {loadingListings ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-white rounded-md border border-[#E2E8E0] overflow-hidden">
                      <div className="aspect-square bg-gray-200/70" />
                      <div className="p-4 space-y-2">
                        <div className="w-3/4 h-4 bg-gray-200 rounded" />
                        <div className="w-1/2 h-3 bg-gray-100 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : normalListings.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {normalListings.slice(0, 8).map((item) => (
                    <Link
                      key={item.id}
                      to={`/listing/${item.id}`}
                      className="group bg-white rounded-md border border-[#E2E8E0] overflow-hidden hover:border-[#C2A04B] hover:shadow-lg transition-all"
                    >
                      <div className="aspect-square overflow-hidden bg-[#EDF0EC]">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-line text-[#C2A04B]/50 text-3xl" />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-bold text-gray-800 line-clamp-1 group-hover:text-[#1F4E3D] transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                          {item.content?.substring(0, 60) || "暂无描述"}
                        </p>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-[11px] px-2 py-0.5 bg-[#1F4E3D]/5 text-[#1F4E3D] rounded-sm font-medium border border-[#1F4E3D]/10">
                            {item.category?.name || "未分类"}
                          </span>
                          <span className="text-[11px] text-gray-400">{formatDate(item.created_at)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-md border border-[#E2E8E0]">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-sm bg-[#1F4E3D]/5 border border-[#1F4E3D]/10">
                    <i className="ri-inbox-line text-2xl text-[#C2A04B]" />
                  </div>
                  <p className="text-gray-500 font-medium">暂无信息</p>
                  <p className="text-gray-400 text-sm mt-1">快来发布第一条信息吧</p>
                </div>
              )}
            </div>

            {/* 右 1/3：置顶推荐侧栏 */}
            <aside className="space-y-5">
              {!loadingListings && pinnedListings.length > 0 && (
                <div className="bg-white rounded-md border border-[#E2E8E0] overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3.5 bg-[#1F4E3D]">
                    <i className="ri-pushpin-line text-[#DDC583]" />
                    <span className="text-sm font-bold text-white">置顶推荐</span>
                  </div>
                  <div className="divide-y divide-[#EDF0EC]">
                    {pinnedListings.slice(0, 4).map((item) => (
                      <Link
                        key={item.id}
                        to={`/listing/${item.id}`}
                        className="group flex items-center gap-3 p-4 hover:bg-[#F7F8F6] transition-colors"
                      >
                        <div className="w-16 h-16 rounded-sm bg-[#EDF0EC] overflow-hidden shrink-0 aspect-square">
                          {item.images?.[0] ? (
                            <img src={item.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className="ri-image-line text-[#C2A04B]/50" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 group-hover:text-[#1F4E3D] transition-colors">{item.title}</h3>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.category?.name || "未分类"} · {formatDate(item.created_at)}
                          </p>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#C2A04B]" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* 发布引导卡 */}
              <div className="bg-gradient-to-br from-[#1F4E3D] to-[#12382A] rounded-md p-6 text-center border border-[#C2A04B]/30">
                <div className="w-12 h-12 mx-auto mb-3 rounded-sm bg-[#C2A04B] flex items-center justify-center shadow-lg shadow-[#C2A04B]/30">
                  <i className="ri-add-line text-[#1F4E3D] text-xl" />
                </div>
                <p className="text-white font-bold text-sm mb-1">需要发布信息？</p>
                <p className="text-white/60 text-xs mb-4">严格审核，快速上架，触达精准用户</p>
                <Link
                  to="/publish"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] text-sm font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all"
                >
                  <i className="ri-send-plane-line" />立即发布
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ======== 第四部分：底部金色分隔 + 工具入口 ======== */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C2A04B]/60 to-transparent mb-8" />
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/category"
              className="group flex items-center gap-4 p-5 bg-white rounded-md border border-[#E2E8E0] hover:border-[#C2A04B] hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-sm bg-[#1F4E3D] flex items-center justify-center shadow-md group-hover:bg-[#C2A04B] transition-colors">
                <i className="ri-grid-line text-[#DDC583] group-hover:text-[#1F4E3D] text-xl" />
              </div>
              <div className="text-left">
                <span className="text-sm font-bold text-gray-800 block">浏览分类</span>
                <p className="text-[11px] text-gray-400 mt-0.5">按行业查找所需信息</p>
              </div>
            </Link>

            <Link
              to="/publish"
              className="group flex items-center gap-4 p-5 bg-white rounded-md border border-[#E2E8E0] hover:border-[#C2A04B] hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#A8863A] flex items-center justify-center shadow-md group-hover:from-[#D8B86A] group-hover:to-[#B8912F] transition-colors">
                <i className="ri-add-line text-[#1F4E3D] text-xl" />
              </div>
              <div className="text-left">
                <span className="text-sm font-bold text-gray-800 block">发布信息</span>
                <p className="text-[11px] text-gray-400 mt-0.5">简单几步完成发布</p>
              </div>
            </Link>
          </div>

          {/* 列表为空时的发布引导 */}
          {!loadingListings && listings.length === 0 && (
            <div className="text-center mt-10">
              <Link
                to="/publish"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-bold rounded-md hover:from-[#D8B86A] hover:to-[#B8912F] transition-all shadow-lg shadow-[#C2A04B]/20"
              >
                <i className="ri-add-line text-lg" />立即发布第一条信息
              </Link>
            </div>
          )}
        </div>
      </section>

      <ExecutiveFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
